import { useMemo, useState } from 'react';
import { Button, Stack } from '@mui/material';
import { Rule } from '@mui/icons-material';
import { PaginationTable } from '../../../common/components';
import {
  useChangeNotificationStatus,
  useNotificationEvents,
  useRetryNotificationEvent,
} from '../../../hooks/useNotifications';
import { DEFAULT_FILTERS } from '../constants/notificationsConstants';
import { EventDetailDialog } from './EventDetailDialog';
import { FiltersBar } from './FiltersBar';
import { InboxTable } from './InboxTable';
import { MetricsBoard } from './MetricsBoard';
import { NotificationsOperationsDialog } from './NotificationsOperationsDialog';
import { exportFilteredNotificationsToCsv } from '../utils/exportNotificationsCsv';

export const Inbox = () => {
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [detailUuid, setDetailUuid] = useState(null);
  const [operationsOpen, setOperationsOpen] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  const apiFilters = useMemo(
    () => ({ ...appliedFilters, page, per_page: rowsPerPage }),
    [appliedFilters, page, rowsPerPage]
  );
  const { data, isLoading, isFetching } = useNotificationEvents(apiFilters);
  const changeStatus = useChangeNotificationStatus();
  const retryEvent = useRetryNotificationEvent();

  const handleAction = (uuid, action, payload = {}) => {
    if (action === 'retry') {
      retryEvent.mutate({ uuid, payload: { channels: ['internal_panel'] } });
      return;
    }
    changeStatus.mutate({ uuid, action, payload });
  };

  const handleExportCsv = async () => {
    try {
      setExportLoading(true);
      await exportFilteredNotificationsToCsv(appliedFilters);
    } catch (error) {
      console.error('Error exporting notifications CSV:', error);
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <Stack >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="flex-end"
        sx={{ bgcolor: 'background.paper', mb: 1.5 }}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <Button variant="contained" startIcon={<Rule />} onClick={() => setOperationsOpen(true)}>
            Operaciones
          </Button>
        </Stack>
      </Stack>
      <MetricsBoard metrics={data?.metrics || {}}/>
      <FiltersBar
        filters={filters}
        setFilters={setFilters}
        onSearch={() => {
          setAppliedFilters(filters);
          setPage(1);
        }}
        onReset={() => {
          setFilters(DEFAULT_FILTERS);
          setAppliedFilters(DEFAULT_FILTERS);
          setPage(1);
        }}
        onExportCsv={handleExportCsv}
        loading={isLoading}
        exportLoading={exportLoading}
        exportDisabled={!data?.total}
      />
      <InboxTable
        events={data?.results || []}
        loading={isLoading || isFetching}
        onView={setDetailUuid}
        onAction={handleAction}
        actionLoading={changeStatus.isPending || retryEvent.isPending}
      />
      <PaginationTable
        totalPages={data?.total_pages || 1}
        totalCount={data?.total || 0}
        page={page}
        onPageChange={(_, value) => setPage(value)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(Number(event.target.value));
          setPage(1);
        }}
      />
      <EventDetailDialog uuid={detailUuid} onClose={() => setDetailUuid(null)} />
      <NotificationsOperationsDialog
        open={operationsOpen}
        onClose={() => setOperationsOpen(false)}
      />
    </Stack>
  );
};
